var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import produce from 'immer';
import { createSelector } from 'reselect';
import getOr from 'lodash/fp/getOr';
import omit from 'lodash/omit';
import migrateState from './migrateState';
export var initialState = {
    page: 1,
    presets: {},
    sorting: [],
    isConfigFormOpen: false,
    uxState: {
        selectedRow: null,
        checkedRows: {}
    },
    columnConfig: {},
    filter: {},
    columnsWidth: {},
    columnsOrder: [],
    defaultSorting: []
};
export var actionPrefix = '@store/CommonGrid';
export var actionTypes = {
    init: "".concat(actionPrefix, "/init"),
    clearUxState: "".concat(actionPrefix, "/clearUxState"),
    handleUxChange: "".concat(actionPrefix, "/handleUxChange"),
    openConfigForm: "".concat(actionPrefix, "/openConfigForm"),
    closeConfigForm: "".concat(actionPrefix, "/closeConfigForm"),
    setColumnConfig: "".concat(actionPrefix, "/setColumnConfig"),
    setPage: "".concat(actionPrefix, "/setPage"),
    setPresets: "".concat(actionPrefix, "/setPresets"),
    createPreset: "".concat(actionPrefix, "/createPreset"),
    deletePreset: "".concat(actionPrefix, "/deletePreset"),
    setRowsPerPage: "".concat(actionPrefix, "/setRowsPerPage"),
    resetFilters: "".concat(actionPrefix, "/resetFilters"),
    addFilter: "".concat(actionPrefix, "/addFilter"),
    setFilters: "".concat(actionPrefix, "/setFilters"),
    addSorting: "".concat(actionPrefix, "/addSorting"),
    clearSettings: "".concat(actionPrefix, "/clearSettings"),
    resetGrid: "".concat(actionPrefix, "/resetGrid"),
    setColumnWidth: "".concat(actionPrefix, "/setColumnWidth"),
    setColumnsOrder: "".concat(actionPrefix, "/setColumnsOrder")
};
var migrationChecked = false;
export default (function (state, action) {
    if (state === void 0) { state = {}; }
    return produce(state, function (draft) {
        var _a, _b, _c;
        var _d, _e, _f, _g, _h;
        // version migrations
        if (!migrationChecked && state && Object.keys(state).length > 0) {
            draft = migrateState(draft);
            migrationChecked = true;
        }
        var gridName = action.gridName;
        if (!gridName)
            return draft; // action does not contains gridName, skip immediatelly
        if (!draft[gridName]) {
            // if this grid is not yet in store, initialize
            draft[gridName] = __assign({}, initialState);
        }
        switch (action.type) {
            case actionTypes.init:
                var defaultSorting = action.initProps.defaultSorting;
                draft[gridName].page = 1;
                if (defaultSorting) {
                    draft[gridName].defaultSorting = defaultSorting;
                    draft[gridName].sorting = ((_d = draft[gridName].sorting) === null || _d === void 0 ? void 0 : _d.length) ? draft[gridName].sorting : defaultSorting;
                }
                break;
            case actionTypes.clearUxState:
                draft[gridName].uxState = initialState.uxState;
                break;
            case actionTypes.handleUxChange:
                draft[gridName].uxState = __assign(__assign({}, draft[gridName].uxState), (_a = {}, _a[action.uxKey] = action.uxValue, _a));
                break;
            case actionTypes.openConfigForm:
                draft[gridName].isConfigFormOpen = true;
                break;
            case actionTypes.closeConfigForm:
                draft[gridName].isConfigFormOpen = false;
                break;
            case actionTypes.setColumnConfig:
                draft[gridName].columnConfig = action.columnConfig;
                draft[gridName].isConfigFormOpen = false;
                break;
            case actionTypes.resetFilters:
                draft[gridName].filter = action.filters;
                break;
            case actionTypes.addFilter:
                var column = action.column, value = action.value, filterProps = action.filterProps;
                var currentFilterState = (_b = {}, _b[column] = { value: value, filterProps: filterProps }, _b);
                draft[gridName].filter = __assign(__assign({}, draft[gridName].filter), currentFilterState);
                draft[gridName].page = 1;
                break;
            case actionTypes.setFilters:
                draft[gridName].filter = action.filters;
                break;
            case actionTypes.addSorting:
                var direction = action.direction;
                var sortingColumn = action.column;
                if (((_g = (_f = (_e = draft[gridName]) === null || _e === void 0 ? void 0 : _e.defaultSorting) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.column) === sortingColumn && direction === null) {
                    direction = 'asc';
                }
                var sorting = [{ column: sortingColumn, direction: direction }];
                if (direction === null) {
                    sorting = ((_h = draft[gridName]) === null || _h === void 0 ? void 0 : _h.defaultSorting) || [];
                }
                draft[gridName].sorting = sorting;
                break;
            case actionTypes.clearSettings:
                draft[gridName].page = initialState.page;
                draft[gridName].filter = initialState.filter;
                draft[gridName].uxState = initialState.uxState;
                break;
            case actionTypes.setPage:
                draft[gridName].page = action.page;
                break;
            case actionTypes.setPresets:
                draft[gridName].presets = action.presets;
                break;
            case actionTypes.createPreset:
                draft[gridName].presets = __assign(__assign({}, (draft[gridName].presets || {})), (_c = {}, _c[action.presetName] = action.filters, _c));
                break;
            case actionTypes.deletePreset:
                draft[gridName].presets = omit(draft[gridName].presets, action.presetName);
                break;
            case actionTypes.setRowsPerPage:
                draft[gridName].rowsPerPage = action.rowsPerPage;
                break;
            case actionTypes.resetGrid:
                draft[gridName] = __assign(__assign({}, initialState), { rowsPerPage: draft[gridName].rowsPerPage });
                break;
            case actionTypes.setColumnWidth:
                if (action.columnsWidth === null) {
                    draft[gridName].columnsWidth = initialState.columnsWidth;
                }
                else {
                    draft[gridName].columnsWidth = __assign(__assign({}, draft[gridName].columnsWidth), action.columnsWidth);
                }
                draft[gridName].columnsWidthVariant = action.columnLayout;
                break;
            case actionTypes.setColumnsOrder:
                draft[gridName].columnsOrder = action.columnsOrder;
                break;
        }
        return draft;
    });
});
export var createActions = function (gridName) { return ({
    init: function (initProps) { return ({ gridName: gridName, type: actionTypes.init, initProps: initProps }); },
    clearUxState: function () { return ({ gridName: gridName, type: actionTypes.clearUxState }); },
    handleUxChange: function (uxKey, uxValue) { return ({
        gridName: gridName,
        type: actionTypes.handleUxChange,
        uxKey: uxKey,
        uxValue: uxValue
    }); },
    openConfigForm: function () { return ({ gridName: gridName, type: actionTypes.openConfigForm }); },
    closeConfigForm: function () { return ({ gridName: gridName, type: actionTypes.closeConfigForm }); },
    setColumnConfig: function (columnConfig) { return ({ gridName: gridName, type: actionTypes.setColumnConfig, columnConfig: columnConfig }); },
    setPage: function (page) { return ({ gridName: gridName, type: actionTypes.setPage, page: page }); },
    setPresets: function (presets) { return ({ gridName: gridName, type: actionTypes.setPresets, presets: presets }); },
    createPreset: function (presetName, filters) { return ({
        gridName: gridName,
        type: actionTypes.createPreset,
        presetName: presetName,
        filters: filters
    }); },
    deletePreset: function (presetName) { return ({ gridName: gridName, type: actionTypes.deletePreset, presetName: presetName }); },
    setColumnWidth: function (columnsWidth, columnLayout) { return ({
        gridName: gridName,
        type: actionTypes.setColumnWidth,
        columnsWidth: columnsWidth,
        columnLayout: columnLayout
    }); },
    resetGrid: function () { return ({ gridName: gridName, type: actionTypes.resetGrid }); },
    setColumnsOrder: function (columnsOrder) { return ({
        gridName: gridName,
        type: actionTypes.setColumnsOrder,
        columnsOrder: columnsOrder
    }); },
    setRowsPerPage: function (rowsPerPage) { return ({ gridName: gridName, type: actionTypes.setRowsPerPage, rowsPerPage: rowsPerPage }); },
    resetFilters: function () { return ({ gridName: gridName, type: actionTypes.resetFilters }); },
    addFilter: function (column, value, filterProps) { return ({
        gridName: gridName,
        type: actionTypes.addFilter,
        column: column,
        value: value,
        filterProps: filterProps
    }); },
    setFilters: function (filters) { return ({ gridName: gridName, type: actionTypes.setFilters, filters: filters }); },
    addSorting: function (column, direction) { return ({
        gridName: gridName,
        type: actionTypes.addSorting,
        column: column,
        direction: direction
    }); },
    clearSettings: function () { return ({ gridName: gridName, type: actionTypes.clearSettings }); }
}); };
export var createSelectors = function (gridName) { return ({
    getUxState: function (state) {
        return getOr(initialState.uxState, "commonGrid.".concat(gridName, ".uxState"), state);
    },
    isConfigFormOpen: function (state) {
        return getOr(initialState.isConfigFormOpen, "commonGrid.".concat(gridName, ".isConfigFormOpen"), state);
    },
    getColumnConfig: function (state) {
        return getOr(initialState.columnConfig, "commonGrid.".concat(gridName, ".columnConfig"), state);
    },
    getPage: function (state) { return getOr(initialState.page, "commonGrid.".concat(gridName, ".page"), state); },
    getPresets: function (state) { return getOr(initialState.presets, "commonGrid.".concat(gridName, ".presets"), state); },
    getRowsPerPage: function (state) {
        return getOr(initialState.rowsPerPage, "commonGrid.".concat(gridName, ".rowsPerPage"), state);
    },
    getFilter: function (state) { return getOr(initialState.filter, "commonGrid.".concat(gridName, ".filter"), state); },
    getSorting: function (state) {
        return getOr(initialState.sorting, "commonGrid.".concat(gridName, ".sorting"), state);
    },
    getColumnsWidth: function (state) {
        return getOr(null, "commonGrid.".concat(gridName, ".columnsWidth"), state);
    },
    getColumnsWidthVariant: function (state) {
        return getOr(undefined, "commonGrid.".concat(gridName, ".columnsWidthVariant"), state);
    },
    getColumnsOrder: function (state) { return getOr([], "commonGrid.".concat(gridName, ".columnsOrder"), state); }
}); };
export var createFullSelector = function (gridName) {
    var selectors = createSelectors(gridName);
    return createSelector(function (state) { return selectors.getUxState(state); }, function (state) { return selectors.isConfigFormOpen(state); }, function (state) { return selectors.getColumnConfig(state); }, function (state) { return selectors.getPage(state); }, function (state) { return selectors.getPresets(state); }, function (state) { return selectors.getRowsPerPage(state); }, function (state) { return selectors.getFilter(state); }, function (state) { return selectors.getSorting(state); }, function (state) { return selectors.getColumnsWidth(state); }, function (state) { return selectors.getColumnsWidthVariant(state); }, function (state) { return selectors.getColumnsOrder(state); }, function (uxState, isConfigFormOpen, columnConfig, page, presets, rowsPerPage, filter, sorting, columnsWidth, columnsWidthVariant, columnsOrder) { return ({
        uxState: uxState,
        isConfigFormOpen: isConfigFormOpen,
        columnConfig: columnConfig,
        page: page,
        presets: presets,
        rowsPerPage: rowsPerPage,
        filter: filter,
        sorting: sorting,
        columnsWidth: columnsWidth,
        columnsWidthVariant: columnsWidthVariant,
        columnsOrder: columnsOrder
    }); });
};
